/**
 * Orchestrates creating a pipeline from the "+ New pipeline" flow (SE-5487).
 *
 * Wraps `useBoardManager.createBoard` with the two post-create behaviors the AC
 * requires:
 *   - Every template "opens directly": `createBoard` already sets the new board
 *     as the active board, so no extra work is needed here for that.
 *   - A CUSTOM (blank) pipeline is created with zero stages, so it would be
 *     unusable on its own — this hook opens the Stage Editor (SE-5488) scoped to
 *     the new board so the manager can define stages immediately (AC2).
 *
 * Kept as a thin, dependency-injected hook (it takes `createBoard`) so it can be
 * unit-tested without standing up the whole board-manager Apollo surface.
 * @module features/player-kanban/hooks/useCreatePipeline
 */

import { useCallback } from "react";

import { useStageEditorPanelState } from "./useStageEditorPanelState";

import { KanbanBoard, KanbanPipelineTemplate } from "@/generated/graphql";

/** A board creator matching `useBoardManager.createBoard`. */
export type CreateBoardFn = (
  name: string,
  pipelineTemplate?: KanbanPipelineTemplate
) => Promise<KanbanBoard>;

/**
 * Create a pipeline from a template, opening the Stage Editor for CUSTOM.
 * @param createBoard - The board creator (from `useBoardManager`).
 * @returns A `createPipeline(name, template)` callback returning the new board.
 */
export function useCreatePipeline(
  createBoard: CreateBoardFn
): (name: string, template: KanbanPipelineTemplate) => Promise<KanbanBoard> {
  const stageEditor = useStageEditorPanelState();

  return useCallback(
    async (name: string, template: KanbanPipelineTemplate) => {
      const board = await createBoard(name, template);

      // Open the Stage Editor for ANY pipeline that arrives with zero stages so
      // a blank board is never a dead end (AC2). This is CUSTOM by design, but
      // also any template whose backend seed is currently missing (e.g.
      // OUTGOING_TRANSFERS seeds 0 stages on dev today — see SE-6108). Reading
      // the real seeded-phase count off the create response, rather than
      // assuming per template, keeps this correct if the BE seed changes.
      if ((board.phases?.length ?? 0) === 0) {
        stageEditor.open(board.id);
      }

      return board;
    },
    [createBoard, stageEditor]
  );
}
